import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import './EmailVerificationView.css';

/**
 * Component for handling email verification links
 * Extracts the key from URL parameters and calls the API to verify
 */
const EmailVerificationView = () => {
  const { key } = useParams();
  const { verifyEmailWithKey, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const verificationAttempted = useRef(false);
  
  const [verificationState, setVerificationState] = useState({
    isLoading: true,
    isVerified: false,
    error: null
  });
  
  useEffect(() => {
    // Only attempt verification once to prevent infinite loops
    if (verificationAttempted.current) {
      return;
    }
    
    const verifyEmail = async () => {
      verificationAttempted.current = true;
      
      try {
        // Call the verification API
        const result = await verifyEmailWithKey(key);
        
        if (result.success) {
          // Handle cases where verification succeeded but user needs to log in
          const needsLogin = result.meta?.is_verified && !result.meta?.is_authenticated;
          
          setVerificationState({
            isLoading: false,
            isVerified: true,
            error: null,
            needsLogin: needsLogin,
            message: result.data?.message
          });
        } else {
          setVerificationState({
            isLoading: false,
            isVerified: false,
            error: 'Email verification failed. Please try again or contact support.'
          });
        }
      } catch (error) {
        // Log the full error for debugging
        // This is intentionally showing in tests when we mock a rejection - it's expected behavior
        // Only log in non-test environments
        if (process.env.NODE_ENV !== 'test') {
          console.error('Email verification error:', error);
        }
        
        // Special case: if this error contains our custom message about different browser verification
        if (error.message && error.message.includes('different browser session')) {
          // Use the verification status object if available, otherwise fallback
          const verificationStatus = error.verificationStatus || {
            success: true,
            meta: { is_verified: true, is_authenticated: false },
            data: { message: "Your email has been verified successfully! Please log in to access your account." }
          };
          
          setVerificationState({
            isLoading: false,
            isVerified: true,
            error: null,
            needsLogin: true,
            message: verificationStatus.data.message
          });
          return;
        }
        
        setVerificationState({
          isLoading: false,
          isVerified: false,
          error: error.message || 'Email verification failed. The link may have expired or is invalid.'
        });
      }
    };
    
    verifyEmail();
  }, [key, verifyEmailWithKey]);
  
  // Render loading state
  if (verificationState.isLoading) {
    return (
      <div className="email-verification">
        <Card className="email-verification__card">
          <div className="email-verification__loading">
            <div className="loading-spinner"></div>
          </div>
          <h2>Verifying Your Email...</h2>
          <p>Please wait while we verify your email address.</p>
        </Card>
      </div>
    );
  }
  
  // Render success state
  if (verificationState.isVerified) {
    return (
      <div className="email-verification">
        <Card className="email-verification__card">
          <div className="email-verification__success-icon">✓</div>
          <h2>Email Verified Successfully!</h2>
          
          {verificationState.needsLogin ? (
            <>
              <p>{verificationState.message || 'Your email has been verified. Please log in to access your account.'}</p>
              <Button 
                onClick={() => navigate('/accounts/login')}
              >
                Go to Login
              </Button>
            </>
          ) : (
            <>
              <p>Your email has been verified and your account is now active.</p>
              <Button 
                onClick={() => navigate('/weaversworld')}
              >
                Continue to Weaver's World
              </Button>
            </>
          )}
        </Card>
      </div>
    );
  }
  
  // Render error state
  return (
    <div className="email-verification">
      <Card className="email-verification__card">
        <div className="email-verification__error-icon">!</div>
        <h2>Verification Failed</h2>
        <p>{verificationState.error}</p>
        <div className="email-verification__actions">
          <Button 
            onClick={() => navigate('/accounts/login')}
          >
            Go to Login
          </Button>
          <Button 
            onClick={() => navigate('/weaversworld')}
          >
            Go to Home
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default EmailVerificationView;